import api from './api';
import { ApiResponse } from '../types/api';
import { Invoice } from '../types/profile';
import { getToken } from '../utils/storage';

/**
 * Utilisation d'une fonctionnalité
 * GET /features/usage
 */
export interface FeatureUsageItem {
  code: string;
  name: string;
  used: number;
  limit: number;
  remaining: number;
  percentage: number;  // 0-100
  isLimitReached: boolean;
}

/**
 * Réponse de l'utilisation des features
 */
export interface FeatureUsageResponse {
  customerId: string;
  planName: string;
  features: Record<string, FeatureUsageItem>;
  period: {
    start: string;
    end: string;
  };
}

/**
 * Paiement retourné par GET /billing/payments
 *  Réponse plate sans enveloppe {success, data}
 * Le champ amount est une string (ex: "19.90")
 */
export interface BillingPayment {
  id: string;
  customerId: string;
  invoiceId: string | null;
  amount: string;
  currency: string;
  paymentMethod: 'mobile_money' | 'bank_transfer' | 'card' | 'cash';
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  transactionId: string;
  paymentGateway: 'serdipay' | 'stripe' | 'manual';
  paymentDate: string;
  notes: string | null;
  gatewayResponse: any;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

/**
 * Service de facturation  customer-facing uniquement
 * Endpoints: GET /billing/invoices, /billing/invoices/:id/detail,
 * /billing/invoices/:id/pdf, /billing/payments, /features/usage
 */
class BillingApiService {
  private baseUrl = '/billing';

  /**
   * Récupère les factures de l'utilisateur connecté
   * GET /billing/invoices
   * Réponse: { success, data[], meta.pagination }
   */
  async getInvoices(params?: {
    page?: number;
    limit?: number;
    status?: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
  }): Promise<{
    invoices: Invoice[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<Invoice[]>>(
      `${this.baseUrl}/invoices`,
      params
    );
    return {
      invoices: response.data || [],
      meta: response.meta?.pagination
        ? { pagination: response.meta.pagination }
        : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }

  /**
   * Détail complet d'une facture (avec paiements liés)
   * GET /billing/invoices/:id/detail
   */
  async getInvoiceDetail(invoiceId: string): Promise<Invoice> {
    const response = await api.get<ApiResponse<Invoice>>(
      `${this.baseUrl}/invoices/${invoiceId}/detail`
    );
    return response.data;
  }

  /**
   * Télécharge la facture en PDF
   * GET /billing/invoices/:id/pdf
   * Réponse: Buffer (text/plain)  utiliser window.URL.createObjectURL(blob)
   */
  async downloadInvoicePdf(invoiceId: string): Promise<Blob> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
    const token = getToken();
    const response = await fetch(`${baseUrl}${this.baseUrl}/invoices/${invoiceId}/pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: '*/*'
      }
    });
    if (!response.ok) {
      throw new Error(`Téléchargement de la facture échoué: ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Récupère l'historique des paiements de l'utilisateur connecté
   * GET /billing/payments
   *  Réponse plate {payments[], total, page, limit}  pas d'enveloppe {success, data}
   */
  async getPayments(params?: { page?: number; limit?: number }): Promise<{
    payments: BillingPayment[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await api.get<{
      payments: BillingPayment[];
      total: number;
      page: number;
      limit: number;
    }>(`${this.baseUrl}/payments`, params);
    return response;
  }

  /**
   * Récupère l'utilisation des features pour le customer de l'utilisateur connecté
   * GET /features/usage
   */
  async getFeatureUsage(): Promise<FeatureUsageResponse> {
    const response = await api.get<ApiResponse<FeatureUsageResponse>>('/features/usage');
    return response.data;
  }
}

export const billingApiService = new BillingApiService();
export default billingApiService;
